import $ from 'jquery';
import Flickity from 'flickity';
import layout from '../Layout';
import scrollLeft from '../helpers/scrollLeft';
import resizeFlickityIfNeeded from '../helpers/flickityHelper';
import Video from './Video';
import ProductZoom from './ProductZoom';

export default class ProductGallery {
  constructor(options) {
    this.$gallery = options.$gallery;
    this.settings = options.settings;
    this.isQuickshop = options.isQuickshop || false;

    // Gallery
    this.flickity = null;
    this.slider = '[data-product-slider]';
    this.$slider = this.$gallery.find(this.slider);
    this.slides = '[data-product-image]';
    this.$slides = this.$gallery.find(this.slides);
    this.$sliderPagination = this.$gallery.find('[data-product-slider-pagination]');
    this.$paginationTrigger = this.$gallery.find('[data-product-thumbnail]');
    this.productZoom = null;

    if (this.settings.enable_image_zoom) {
      this.productZoom = new ProductZoom(options);
    }

    this.videos = {};
    this.videoSlides = this.$slider[0].querySelectorAll('[data-product-image-video]');

    // Re-initialise gallery when crossing breakpoints if needed
    this._onUpdateGallery = this._onUpdateGallery.bind(this);
    layout.onBreakpointChange(this._onUpdateGallery);

    this.slideImageLoaded = this._onSlideImageLoaded.bind(this);
    this.variantChange = this._onVariantChange.bind(this);

    if (this.isQuickshop) {
      this.$gallery.trigger('theme:rimg:watch');
    }

    this._init();
  }

  unload() {
    this.$slider[0].removeEventListener('rimg:load', this.slideImageLoaded);

    if (this.productZoom) {
      this.productZoom.unload();
    }

    if (this.isQuickshop) {
      this.$gallery.trigger('theme:rimg:unwatch');
    }

    if (this.isQuickshop) {
      this._onUpdateGallery();
    }

    layout.offBreakpointChange(this._onUpdateGallery);

    this._unloadGallery();
  }

  _init() {
    this.$slider[0].addEventListener('rimg:load', this.slideImageLoaded);

    if (this.videoSlides.length) {
      for (let i = 0; i < this.videoSlides.length; i += 1) {
        const videoSlide = this.videoSlides[i];
        const slideId = videoSlide.getAttribute('data-product-image');
        const videoEl = videoSlide.querySelector('[data-video]');

        this.videos[slideId] = new Video(videoEl);
      }
    }

    if (this.$slides.length) {
      this._initGallery();
    }
  }

  /**
   * Make sure Flickity updates viewport height when images load in
   * @private
   */
  _onSlideImageLoaded() {
    resizeFlickityIfNeeded(this.flickity);
  }

  _unloadGallery() {
    if (this.flickity) {
      for (let i = 0; i < this.videos.length; i += 1) {
        this.videos[i].unload();
      }

      this.$paginationTrigger.off('.gallery');

      this.flickity.destroy();
      this.flickity = null;
      this.$slider.find(this.slides).attr('style', '');
    }
  }

  _onUpdateGallery() {
    /**
      Do not reset the gallery if we are taking an element full-screen.
      This function is called by layout.onBreakpointChange, a side effect 
      of going full-screen with a video. If there is a full-screen element 
      just return out of this function. 
    */
    if (document.fullscreenElement !== null) {
      return;
    }

    this._unloadGallery();
    this._initGallery();
  }

  /**
   * Update gallery on variant change
   *
   * @param variant
   * @property {Object} options.featured_image - Featured image information
   * @private
   */
  _onVariantChange(variant) {
    if (!variant.featured_image || !this.flickity) {
      return;
    }

    const index = this.$slider
      .find(`[data-product-image=${variant.featured_image.id}]`)
      .attr('data-product-image-index');
    this.flickity.select(index, false, false);
  }

  _initGallery() {
    const $selectedImage = this.$slider.find('.image--selected');

    // Options shared on Desktop and Mobile
    let flickityOptions = {
      autoPlay: 0,
      accessibility: true,
      adaptiveHeight: true,
      cellAlign: 'left',
      cellSelector: this.slides,
      pageDots: false,
      prevNextButtons: false,
      wrapAround: true,
    };

    // Mobile only options
    const optionsMobile = {
      draggable: true,
      friction: 0.8,
      selectedAttraction: 0.2,
    };

    // Desktop only options
    const optionsDesktop = {
      draggable: false,
    };

    if (layout.isBreakpoint('XS')){
      flickityOptions = $.extend({}, optionsMobile, flickityOptions);
    } else {
      flickityOptions = $.extend({}, optionsDesktop, flickityOptions);
    }

    // If a variant has an image pre-selected, start the slideshow at that image
    // If the slideshow has been re-initalised across a break point, slideshow
    // restarts at same slide
    if ($selectedImage.length) {
      const initialIndex = parseInt($selectedImage.attr('data-product-image-index'), 10);

      flickityOptions = $.extend({}, { initialIndex }, flickityOptions);
    }

    this.flickity = new Flickity(this.$slider[0], flickityOptions);

    this._bindGallery();
    this._handleGalleryVideos();
  }

  _bindGallery() {
    this.flickity.on('select', () => {
      this._updateActiveSlide(this.flickity.selectedIndex);
      this._handleGalleryVideos();
    });

    this.$paginationTrigger.on('click.gallery', (event) => {
      event.preventDefault();
      const $target = $(event.currentTarget);
      this.flickity.select($target.attr('data-product-thumbnail'), false, false);
    });
  }

  /**
   * Handle gallery videos
   * - Pause video if is not active slide
   * - Play video if is active slide, and is autoplay
   *
   * @private
   */
  _handleGalleryVideos() {
    const slideId = parseInt(this.flickity.selectedElement.getAttribute('data-product-image'), 10);

    Object.keys(this.videos).forEach((key) => {
      const videoKey = parseInt(key, 10);

      // Pause inactive slide videos
      if (slideId !== videoKey) {
        this.videos[videoKey].onPauseClick();
      }

      // Play video if is selected, and auto play enabled
      if (slideId === videoKey && this.settings.enable_video_autoplay) {
        this.videos[videoKey].autoplay();
      }
    });
  }

  /**
   * Adds a non-flickity active class to thumbnails, and main slideshow
   *  - Is used on re-init across breakpoints
   *  - Is used to show the active thumbnail in the pagination
   *
   * @param index
   * @private
   */
  _updateActiveSlide(index) {
    const $newSlide = this.$slider.find(`[data-product-image-index=${index}]`);
    const $newThumbnail = this.$sliderPagination.find(`[data-product-thumbnail=${index}]`);

    $newSlide
      .addClass('image--selected')
      .attr('aria-hidden', 'false')
      .siblings('.image--selected')
      .removeClass('image--selected')
      .attr('aria-hidden', 'true');

    $newThumbnail
      .addClass('thumbnail--selected')
      .siblings('.thumbnail--selected')
      .removeClass('thumbnail--selected');

    if (layout.isBreakpoint('XS')) {
      scrollLeft($newThumbnail);
    }

    if(this.settings.enable_image_zoom) {
      this.productZoom.enableZoom(index);
    }
  }
}
